
import React, { useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
// Adicionado ShieldCheck aos imports do lucide-react
import { Activity, Zap, X, Volume2, Send, HelpCircle, Loader2, Moon, ShieldCheck, MessageSquareOff } from 'lucide-react';
import { Order, Driver, SystemConfig, OperatingContext, SmartSplitSuggestion } from '../types';
import { useSofia } from '../hooks/useSofia';
import { useRetentionMonitor } from '../hooks/useRetentionMonitor';

// --- NEW COMPONENT: Formatted Message Renderer ---
const FormattedMessage = ({ text }: { text: string }) => {
    const boldPattern = /\*\*(.*?)\*\*/g;
    const listPattern = /^\s*[-•]\s+(.*)$/gm;
    const lines = text.split('\n').filter(l => l.trim() !== '');

    const renderLine = (line: string, index: number) => {
        if (line.match(listPattern)) {
            const content = line.replace(/^\s*[-•]\s+/, '');
            return (
                <div key={index} className="flex items-start gap-2 ml-1 mb-1">
                    <span className="text-sal-cyber-teal mt-1.5 text-xs">●</span>
                    <span className="flex-1">{renderInline(content)}</span>
                </div>
            );
        }
        return <p key={index} className="mb-2 last:mb-0 leading-relaxed">{renderInline(line)}</p>;
    };

    const renderInline = (text: string) => {
        const parts = text.split(boldPattern);
        return parts.map((part, i) => {
            if (i % 2 === 1) {
                return <strong key={i} className="text-sal-cyber-teal font-bold">{part}</strong>;
            } else {
                const highlighted = part.split(/(\b(?:Frota|App|Externo|R\$|Economia|Bumerangue|Retenção)\b.*?)/g).map((subPart, j) => {
                    if (subPart.includes('R$')) return <span key={j} className="text-status-ready font-bold">{subPart}</span>;
                    if (subPart.includes('Frota')) return <span key={j} className="text-sal-solid-indigo font-bold">{subPart}</span>;
                    if (subPart.includes('App') || subPart.includes('Externo')) return <span key={j} className="text-sal-burnt-orange font-bold">{subPart}</span>;
                    if (subPart.includes('Economia')) return <span key={j} className="text-status-ready font-bold">{subPart}</span>;
                    if (subPart.includes('Bumerangue')) return <span key={j} className="text-sal-burnt-orange font-bold underline decoration-dotted">{subPart}</span>;
                    return subPart;
                });
                return <span key={i}>{highlighted}</span>;
            }
        });
    };

    return <div className="text-[13px] leading-snug text-text-secondary">{lines.map(renderLine)}</div>;
};

interface Props {
    orders: Order[];
    drivers: Driver[];
    config: SystemConfig;
    context: OperatingContext;
    logisticAnalysis?: SmartSplitSuggestion | null;
    actions: any;
}

export const JarvisHUD: React.FC<Props> = (props) => {
    const {
        isOpen,
        setIsOpen,
        isThinking,
        conversationHistory,
        handleProcessCommandText,
        isProactiveSpeaking
    } = useSofia(
        props.orders,
        props.drivers,
        props.config,
        props.context,
        props.actions,
        props.logisticAnalysis
    );

    // Integração do Monitor de Retenção - dispara avisos quando pedidos retidos devem ser liberados
    useRetentionMonitor(
        props.orders,
        props.config,
        props.logisticAnalysis
    );

    const [textInput, setTextInput] = React.useState('');
    const chatHistoryRef = useRef<HTMLDivElement>(null);

    // Regra Zero-Waste: Verifica se houver pedidos para liberar o chat
    const isHibernating = useMemo(() => {
        const active = props.orders.filter(o => !['delivered', 'archived', 'canceled'].includes(o.status));
        return active.length === 0;
    }, [props.orders]);

    useEffect(() => {
        if (chatHistoryRef.current) {
            chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
        }
    }, [conversationHistory, isOpen]);

    const handleQuickAction = () => {
        if (isHibernating) return;
        setIsOpen(true);
        handleProcessCommandText("O que eu faço?");
    };

    return (
        <>
            <AnimatePresence>
                {!isOpen && !isHibernating && (
                    <motion.button
                        initial={{ opacity: 0, scale: 0.8, x: 20 }}
                        animate={{ opacity: 1, scale: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.8, x: 20 }}
                        onClick={handleQuickAction}
                        className="fixed bottom-6 md:bottom-8 right-24 bg-sal-cyber-teal text-white px-4 py-2 rounded-l-full rounded-tr-full shadow-[var(--shadow-lg)] z-[59] font-bold text-xs md:text-sm uppercase tracking-wide flex items-center gap-2 hover:brightness-110 transition-all border-2 border-app-bg"
                    >
                        <HelpCircle className="w-4 h-4" />
                        O QUE EU FAÇO?
                    </motion.button>
                )}
            </AnimatePresence>

            <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsOpen(!isOpen)}
                className={`fixed bottom-4 md:bottom-6 right-4 md:right-6 w-14 md:w-16 h-14 md:h-16 rounded-full bg-card-bg border-2 flex items-center justify-center z-[60] shadow-[var(--shadow-lg)] transition-all group ${isOpen ? 'border-sal-cyber-teal scale-110' : isHibernating ? 'border-border-dim opacity-60 grayscale' : 'border-sal-cyber-teal/50 hover:scale-105'}`}
            >
                <div className={`absolute inset-1 rounded-full border-2 border-dashed animate-[spin_10s_linear_infinite] ${isHibernating ? 'border-border-dim/30' : 'border-sal-cyber-teal/30'}`} />
                <div className={`absolute inset-[10px] rounded-full backdrop-blur-sm flex items-center justify-center ${isHibernating ? 'bg-input-bg/40' : 'bg-sal-cyber-teal/10'}`}>
                    {isHibernating ? (
                        <Moon className="w-5 h-5 text-text-muted" />
                    ) : (
                        <div className="w-6 h-6 md:w-8 md:h-8 bg-sal-cyber-teal rounded-full shadow-[0_0_15px_rgba(6,182,212,0.8)] animate-pulse" />
                    )}
                </div>
            </motion.button>

            <AnimatePresence>
                {!isOpen && isProactiveSpeaking && (
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className="fixed bottom-6 right-24 bg-card-bg border border-sal-cyber-teal/30 text-sal-cyber-teal px-4 py-2 rounded-xl shadow-xl z-50 text-xs font-bold max-w-[200px] truncate pointer-events-none backdrop-blur-md flex items-center gap-2"
                    >
                        <Volume2 className="w-3 h-3 animate-pulse" />
                        <span>Sofia Falando...</span>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 100, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 100, scale: 0.9 }}
                        className="fixed bottom-20 md:bottom-24 right-4 md:right-6 w-[calc(100%-2rem)] md:w-96 bg-card-bg/85 dark:bg-sal-gunmetal/90 backdrop-blur-2xl border border-border-dim rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col max-h-[70vh] md:max-h-[500px]"
                    >
                        <div className="bg-sal-gunmetal-light/5 p-4 flex justify-between items-center border-b border-border-dim shrink-0">
                            <div className="flex items-center gap-2">
                                <Activity className={`w-4 h-4 ${isHibernating ? 'text-text-muted' : 'text-sal-cyber-teal'}`} />
                                <span className="text-xs font-bold text-text-primary tracking-widest uppercase">
                                    {isHibernating ? 'SOFIA EM HIBERNAÇÃO' : 'SISTEMA SOFIA v3.5'}
                                </span>
                            </div>
                            <button onClick={() => setIsOpen(false)} className="hover:bg-sal-gunmetal-light/10 rounded p-1 transition-colors"><X className="w-4 h-4 text-text-muted" /></button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[150px] bg-app-bg/20 no-scrollbar" ref={chatHistoryRef}>
                            {isHibernating && conversationHistory.length === 0 ? (
                                <div className="text-center text-text-muted text-sm py-10 opacity-60">
                                    <Moon className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                    <p className="font-black uppercase tracking-widest">Protocolo Desperdício Zero Ativo</p>
                                    <p className="mt-2 text-xs leading-relaxed">Sensores em em espera. Interações bloqueadas enquanto o hub estiver vazio para economizar recursos neurais.</p>
                                </div>
                            ) : conversationHistory.length === 0 ? (
                                <div className="text-center text-text-muted text-sm py-10 opacity-60">
                                    <Zap className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p>Sistemas online. Como posso ajudar na sua logística?</p>
                                </div>
                            ) : null}

                            {conversationHistory.map((msg, i) => (
                                <div key={i} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[85%] p-3 rounded-xl ${msg.type === 'user' ? 'bg-sal-gunmetal-light/10 text-text-primary rounded-tr-none' : 'bg-sal-cyber-teal/10 border border-sal-cyber-teal/20 rounded-tl-none'}`}>
                                        {msg.type === 'sofia' ? <FormattedMessage text={msg.text} /> : <span className="text-sm font-medium">{msg.text}</span>}
                                    </div>
                                </div>
                            ))}
                            {isThinking && (
                                <div className="flex justify-start">
                                    <div className="flex items-center gap-2 text-xs text-text-muted">
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        <span>Processando...</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-3 bg-card-bg border-t border-border-dim shrink-0">
                            {isHibernating ? (
                                <div className="bg-sal-crimson-alert/5 border border-sal-crimson-alert/20 rounded-lg p-3 flex items-center gap-3">
                                    <MessageSquareOff className="w-5 h-5 text-sal-crimson-alert shrink-0" />
                                    <p className="text-xs font-black text-sal-crimson-alert uppercase leading-tight">Envie um pedido para despertar a IA e liberar o chat operacional.</p>
                                </div>
                            ) : (
                                <div className="flex gap-2">
                                    <div className="relative flex-1">
                                        <input
                                            type="text"
                                            value={textInput}
                                            onChange={e => setTextInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && textInput.trim()) {
                                                    handleProcessCommandText(textInput);
                                                    setTextInput('');
                                                }
                                            }}
                                            placeholder="Digite comandos logísticos..."
                                            className="w-full bg-input-bg border border-input-border rounded-lg pl-3 pr-10 py-2.5 text-sm text-text-primary focus:outline-none focus:border-sal-cyber-teal"
                                        />
                                        <button onClick={() => { if (textInput.trim()) { handleProcessCommandText(textInput); setTextInput(''); } }} disabled={!textInput.trim() || isThinking} className="absolute right-1 top-1 p-1.5 bg-sal-cyber-teal/10 hover:bg-sal-cyber-teal/20 text-sal-cyber-teal rounded disabled:opacity-30"><Send className="w-3.5 h-3.5" /></button>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between items-center mt-2 px-1">
                                <div className="flex items-center gap-1.5">
                                    {/* Ícone ShieldCheck corrigido e importado */}
                                    <ShieldCheck className="w-3 h-3 text-text-muted" />
                                    <span className="text-xs text-text-muted uppercase font-black tracking-widest">Política de Uso Profissional</span>
                                </div>
                                {!isHibernating && (
                                    <button onClick={() => handleProcessCommandText("O que eu faço?")} className="text-xs md:text-xs bg-sal-gunmetal-light/5 hover:bg-sal-gunmetal-light/10 px-2 py-1 rounded text-text-muted hover:text-sal-cyber-teal transition-colors uppercase font-bold tracking-wide">O QUE EU FAÇO?</button>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
